import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReAuthModal } from '../../components/ReAuthModal';
import { PHONE_DIAL_CODES } from '../../data/phoneDialCodes';
import type { Profile } from '../../types/profile';
import { supabase } from '../../lib/supabase';
import { sendWhatsApp } from '../../server/api';
import { cardClass, inputClass, labelClass, lockedFieldShellStyle } from './styles';
import { IconPencil14 } from './SecurityFieldIcons';

function parseWhatsappStored(full: string | null): { dial: string; national: string } {
  if (!full?.trim()) return { dial: '+1', national: '' };
  const s = full.trim();
  if (!s.startsWith('+')) return { dial: '+1', national: s.replace(/\D/g, '') };
  const sorted = [...PHONE_DIAL_CODES].sort((a, b) => b.dial.length - a.dial.length);
  for (const { dial } of sorted) {
    if (s.startsWith(dial)) {
      return { dial, national: s.slice(dial.length).replace(/\D/g, '') };
    }
  }
  return { dial: '+1', national: s.replace(/\D/g, '') };
}

function buildE164(dial: string, nationalDigits: string): string {
  const d = nationalDigits.replace(/\D/g, '');
  return `${dial}${d}`;
}

type AlertFreq = 'immediate' | 'daily' | 'weekly';

function normalizeFrequency(raw: string | null | undefined): AlertFreq {
  const s = (raw ?? 'immediate').toLowerCase();
  if (s === 'daily' || s === 'weekly') return s;
  return 'immediate';
}

type Props = {
  userProfile: Profile | null;
  userId: string;
  userEmail: string;
  onToast: (message: string, variant: 'success' | 'error') => void;
  onProfileSaved: () => Promise<void>;
};

function Switch({
  checked,
  onChange,
  id,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  id: string;
  disabled?: boolean;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`inline-flex h-7 w-12 shrink-0 items-center rounded-full border px-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 disabled:opacity-50 ${
        checked ? 'border-accent bg-accent/20' : 'border-border bg-navy'
      }`}
    >
      <span
        className={`h-6 w-6 rounded-full shadow transition-transform ${
          checked ? 'translate-x-5 bg-accent' : 'translate-x-0 bg-foreground'
        }`}
      />
    </button>
  );
}

export function AlertPreferencesTab({ userProfile, userId, userEmail, onToast, onProfileSaved }: Props) {
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [whatsappAlerts, setWhatsappAlerts] = useState(false);
  const [dialCode, setDialCode] = useState('+1');
  const [phoneNational, setPhoneNational] = useState('');
  const [alertFrequency, setAlertFrequency] = useState<AlertFreq>('immediate');
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [sendingTestWa, setSendingTestWa] = useState(false);
  const [prefsError, setPrefsError] = useState<string | null>(null);
  const [waUnlocked, setWaUnlocked] = useState(false);
  const [reAuthWaOpen, setReAuthWaOpen] = useState(false);
  const [savingWa, setSavingWa] = useState(false);

  const dialOptions = useMemo(() => PHONE_DIAL_CODES, []);

  const applyWhatsappFromProfile = useCallback((p: Profile | null) => {
    const parsed = parseWhatsappStored(p?.whatsapp_number ?? null);
    setDialCode(parsed.dial);
    setPhoneNational(parsed.national);
  }, []);

  useEffect(() => {
    if (!userProfile) return;
    setEmailAlerts(userProfile.email_alerts);
    setWhatsappAlerts(userProfile.whatsapp_alerts);
    applyWhatsappFromProfile(userProfile);
    setAlertFrequency(normalizeFrequency(userProfile.alert_frequency));
    setWaUnlocked(false);
  }, [userProfile, applyWhatsappFromProfile]);

  const setWhatsappAlertsSafe = useCallback(
    (next: boolean) => {
      setWhatsappAlerts(next);
      if (!next) setWaUnlocked(false);
    },
    []
  );

  function whatsappDisplayLine(): string {
    const digits = phoneNational.replace(/\D/g, '');
    if (digits.length === 0) return '—';
    return `${dialCode} ${phoneNational}`.trim();
  }

  async function handleSaveWhatsAppNumber() {
    const e164 = buildE164(dialCode, phoneNational);
    const digits = phoneNational.replace(/\D/g, '');
    if (digits.length < 6) {
      onToast('Enter a valid WhatsApp number (at least 6 digits).', 'error');
      return;
    }
    if (!e164.startsWith('+') || e164.length < 8) {
      onToast('Enter a valid WhatsApp number.', 'error');
      return;
    }

    setSavingWa(true);
    const { error } = await supabase.from('profiles').update({ whatsapp_number: e164 }).eq('id', userId);
    setSavingWa(false);

    if (error) {
      onToast(error.message || 'Could not save number.', 'error');
      return;
    }
    setWaUnlocked(false);
    onToast('WhatsApp number updated', 'success');
    await onProfileSaved();
  }

  function cancelWhatsAppEdit() {
    applyWhatsappFromProfile(userProfile);
    setWaUnlocked(false);
  }

  async function handleSendTestWhatsApp() {
    const saved = userProfile?.whatsapp_number?.trim();
    if (!saved || saved.length < 8) {
      onToast('Save a WhatsApp number first, then send a test.', 'error');
      return;
    }
    const testMessage =
      '👋 Hello from ShieldSME! Your WhatsApp alerts are now active. You will receive security alerts here when threats are detected.';
    setSendingTestWa(true);
    try {
      const data = await sendWhatsApp(saved, testMessage);
      if (data?.success) {
        onToast('Test message sent! Check your WhatsApp.', 'success');
      } else {
        onToast(
          typeof data?.error === 'string' ? data.error : 'Could not send test message.',
          'error'
        );
      }
    } catch {
      onToast('Could not send test message.', 'error');
    } finally {
      setSendingTestWa(false);
    }
  }

  async function handleSavePreferences(e: React.FormEvent) {
    e.preventDefault();
    setPrefsError(null);

    if (whatsappAlerts) {
      const digits = phoneNational.replace(/\D/g, '');
      if (digits.length < 6) {
        setPrefsError('Enter a WhatsApp number or turn off WhatsApp alerts.');
        onToast('Enter a WhatsApp number or turn off WhatsApp alerts.', 'error');
        return;
      }
    }

    const e164 = buildE164(dialCode, phoneNational);

    const payload = {
      email_alerts: emailAlerts,
      whatsapp_alerts: whatsappAlerts,
      whatsapp_number: whatsappAlerts ? e164 : userProfile?.whatsapp_number ?? null,
      alert_frequency: alertFrequency,
    };

    setSavingPrefs(true);
    const { error } = await supabase.from('profiles').update(payload).eq('id', userId);
    setSavingPrefs(false);

    if (error) {
      onToast(error.message || 'Could not save preferences.', 'error');
      return;
    }
    onToast('Changes saved', 'success');
    await onProfileSaved();
  }

  return (
    <div className={cardClass}>
      <h2 className="text-lg font-semibold text-foreground sm:text-xl">How Should We Alert You?</h2>
      <p className="mt-1 text-sm text-muted">
        Choose how and when you want to be notified when something happens.
      </p>

      <form onSubmit={(e) => void handleSavePreferences(e)} className="mt-8 space-y-8" noValidate>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-foreground">Email Alerts</p>
            <p className="mt-1 text-sm text-muted">Receive alerts at your registered business email</p>
          </div>
          <Switch
            id="toggle-email-alerts"
            checked={emailAlerts}
            onChange={setEmailAlerts}
          />
        </div>

        <div className="border-t border-border pt-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground">WhatsApp Alerts</p>
              <p className="mt-1 text-sm text-muted">
                Receive instant alerts on WhatsApp — recommended for urgent threats
              </p>
            </div>
            <Switch
              id="toggle-whatsapp-alerts"
              checked={whatsappAlerts}
              onChange={setWhatsappAlertsSafe}
            />
          </div>

          <ReAuthModal
            open={reAuthWaOpen}
            onClose={() => setReAuthWaOpen(false)}
            onSuccess={() => setWaUnlocked(true)}
            fieldName="WhatsApp number"
            userEmail={userEmail}
          />

          {whatsappAlerts ? (
            <div className="mt-6 space-y-4 rounded-xl border border-border bg-navy/40 p-4 sm:p-5">
              {!waUnlocked ? (
                <>
                  <div>
                    <span className={labelClass}>WhatsApp number</span>
                    <div style={lockedFieldShellStyle} className="mt-1.5 w-full">
                      <span className="min-w-0 truncate text-sm">{whatsappDisplayLine()}</span>
                      <button
                        type="button"
                        onClick={() => setReAuthWaOpen(true)}
                        className="shrink-0 cursor-pointer rounded p-1 transition hover:bg-foreground/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                        title="Click to edit — requires password confirmation"
                        aria-label="Edit WhatsApp number"
                      >
                        <IconPencil14 style={{ color: 'var(--accent-green)' }} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      disabled={sendingTestWa}
                      onClick={() => void handleSendTestWhatsApp()}
                      className="rounded-lg border border-border bg-navy px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-foreground/5 disabled:opacity-60"
                    >
                      {sendingTestWa ? 'Sending…' : 'Send Test Message'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-[minmax(0,11rem)_1fr]">
                    <div>
                      <label htmlFor="wa-dial" className={labelClass}>
                        Country code
                      </label>
                      <select
                        id="wa-dial"
                        value={dialCode}
                        onChange={(e) => setDialCode(e.target.value)}
                        className={`${inputClass} appearance-none bg-navy border-2 border-accent ring-1 ring-accent/25`}
                      >
                        {dialOptions.map((o) => (
                          <option key={`${o.dial}-${o.label}`} value={o.dial}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="wa-phone" className={labelClass}>
                        Phone number
                      </label>
                      <input
                        id="wa-phone"
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="Your WhatsApp number"
                        value={phoneNational}
                        onChange={(e) => setPhoneNational(e.target.value)}
                        className={`${inputClass} border-2 border-accent ring-1 ring-accent/25`}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={savingWa}
                      onClick={() => void handleSaveWhatsAppNumber()}
                      className="rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-cta-text transition hover:bg-accent/90 disabled:opacity-60"
                    >
                      {savingWa ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      disabled={savingWa}
                      onClick={cancelWhatsAppEdit}
                      className="rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-foreground/5 disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      disabled={sendingTestWa}
                      onClick={() => void handleSendTestWhatsApp()}
                      className="rounded-lg border border-border bg-navy px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-foreground/5 disabled:opacity-60"
                    >
                      {sendingTestWa ? 'Sending…' : 'Send Test Message'}
                    </button>
                  </div>
                </>
              )}
              <p className="text-xs text-muted">
                We only send security alerts. No marketing messages.
              </p>
            </div>
          ) : null}
        </div>

        <fieldset className="border-t border-border pt-8">
          <legend className={labelClass}>Alert Frequency</legend>
          <div className="mt-4 space-y-3">
            <label className="flex cursor-pointer gap-3 rounded-lg border border-transparent p-2 transition hover:bg-foreground/[0.03] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/40">
              <input
                type="radio"
                name="alertFrequency"
                value="immediate"
                checked={alertFrequency === 'immediate'}
                onChange={() => setAlertFrequency('immediate')}
                className="mt-1 border-border text-accent focus:ring-accent"
              />
              <span className="text-sm text-foreground">
                <span className="font-medium">Immediately</span> — as soon as a threat is detected
                (recommended)
              </span>
            </label>
            <label className="flex cursor-pointer gap-3 rounded-lg border border-transparent p-2 transition hover:bg-foreground/[0.03] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/40">
              <input
                type="radio"
                name="alertFrequency"
                value="daily"
                checked={alertFrequency === 'daily'}
                onChange={() => setAlertFrequency('daily')}
                className="mt-1 border-border text-accent focus:ring-accent"
              />
              <span className="text-sm text-foreground">
                <span className="font-medium">Daily summary</span> — one email every morning with the
                previous day&apos;s alerts
              </span>
            </label>
            <label className="flex cursor-pointer gap-3 rounded-lg border border-transparent p-2 transition hover:bg-foreground/[0.03] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/40">
              <input
                type="radio"
                name="alertFrequency"
                value="weekly"
                checked={alertFrequency === 'weekly'}
                onChange={() => setAlertFrequency('weekly')}
                className="mt-1 border-border text-accent focus:ring-accent"
              />
              <span className="text-sm text-foreground">
                <span className="font-medium">Weekly summary</span> — every Monday morning with the
                previous week&apos;s alerts
              </span>
            </label>
          </div>
        </fieldset>

        {prefsError ? (
          <p className="text-sm text-danger" role="alert">
            {prefsError}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={savingPrefs}
          className="flex w-full min-h-[48px] items-center justify-center rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-cta-text transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {savingPrefs ? 'Saving…' : 'Save Preferences'}
        </button>
      </form>
    </div>
  );
}
